import React from "react";
import { buildColor, Icon } from "@tvg/design-system";
import { HostedFieldState } from "@tvg/sh-lib-paws/types/braintree";
import { renderError } from "../FundsModal/common/InputField/methods";
import SpinningWheel from "../SpinningWheel";
import {
  Container,
  FieldGroup,
  Label,
  FieldContainer,
  CvvIconContainer,
  LoadingContainer
} from "./styled-components";

interface BraintreeCvvFieldProps {
  isReady: boolean;
  isLoading: boolean;
  cvvFieldState: HostedFieldState;
  containerRef?: (node: HTMLElement | null) => void;
  isDisabled?: boolean;
  onIconClick?: () => void;
}

/**
 * Renders the Braintree-controlled CVV iframe container.
 * The parent is responsible for initialising `useBraintreeCvvHostedField`
 * and passing the resulting state down as props. `containerRef` lets the hook
 * detect when the `#braintree-vault-cvv` node is mounted before initialising.
 */
const BraintreeCvvField = ({
  isReady,
  isLoading,
  cvvFieldState,
  containerRef,
  isDisabled = false,
  onIconClick
}: BraintreeCvvFieldProps) => {
  const [hasVisited, setHasVisited] = React.useState(false);

  React.useEffect(() => {
    if (cvvFieldState.isFocused) {
      setHasVisited(true);
    }
  }, [cvvFieldState.isFocused]);

  const cvvError = (() => {
    if (!hasVisited || cvvFieldState.isFocused) return undefined;
    if (cvvFieldState.isEmpty) return "CVV is required";
    if (!cvvFieldState.isValid) return "CVV is invalid";
    return undefined;
  })();

  return (
    <Container data-qa-label="braintree-cvv-field">
      {(isLoading || !isReady) && (
        <LoadingContainer data-qa-label="braintree-cvv-field-loading">
          <SpinningWheel />
        </LoadingContainer>
      )}
      <FieldGroup isHidden={!isReady}>
        <Label
          id="label-braintree-vault-cvv"
          data-qa-label="label-braintree-vault-cvv"
        >
          CVV
        </Label>
        <FieldContainer
          ref={containerRef}
          id="braintree-vault-cvv"
          role="group"
          aria-labelledby="label-braintree-vault-cvv"
          data-qa-label="braintree-vault-cvv"
          isFocused={cvvFieldState.isFocused}
          hasError={!!cvvError}
          isDisabled={isDisabled}
        >
          {isReady && onIconClick && (
            <CvvIconContainer
              data-qa-label="braintree-vault-cvv-icon"
              role="button"
              tabIndex={0}
              aria-label="CVV information"
              onMouseDown={(event) => event.preventDefault()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onIconClick();
                }
              }}
              onClick={onIconClick}
            >
              <Icon
                name="cvv"
                size="xl"
                lineColor={buildColor("blue_accent", "500")}
              />
            </CvvIconContainer>
          )}
        </FieldContainer>
        {cvvError && renderError(cvvError, "braintree-vault-cvv")}
      </FieldGroup>
    </Container>
  );
};

BraintreeCvvField.displayName = "BraintreeCvvField";

export default BraintreeCvvField;
